// src/pages/DepositWithdraw.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpCircle,
  ArrowDownCircle,
  Upload,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Copy,
  Check
} from 'lucide-react';

export default function DepositWithdraw() {
  const [user, setUser] = useState({ first_name: '', last_name: '' });
  const [balance, setBalance] = useState({ balance_rial: 0, balance_gold: 0 });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [amount, setAmount] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('deposit');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [cardCopied, setCardCopied] = useState(false);
  const [shebaCopied, setShebaCopied] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  // --- نقشه لوگوهای بانک ---
  const bankLogos = {
    'انصار': '/bank-logos/Ansar.svg',
    'آینده': '/bank-logos/Ayandeh.svg',
    'بانک مرکزی': '/bank-logos/Bank_Markazi.svg',
    'کاسپین': '/bank-logos/Caspian.svg',
    'دی': '/bank-logos/Dey.svg',
    'اقتصاد نوین': '/bank-logos/Eghtesad_Novin.svg',
    'فیوچربانک': '/bank-logos/Futurebank.svg',
    'گردشگری': '/bank-logos/Gardeshgari.svg',
    'قوامین': '/bank-logos/Ghavamin.svg',
    'حکمت': '/bank-logos/Hekmat.svg',
    'ایران اروپا': '/bank-logos/Iran_Europe.svg',
    'ایران ونزوئلا': '/bank-logos/Iran_Venezuela.svg',
    'ایران زمین': '/bank-logos/Iran_Zamin.svg',
    'کارآفرین': '/bank-logos/Karafarin.svg',
    'کشاورزی': '/bank-logos/Keshavarzi.svg',
    'خاورمیانه': '/bank-logos/Khavar_Mianeh.svg',
    'کوشار': '/bank-logos/Kosar.svg',
    'مسکن': '/bank-logos/Maskan.svg',
    'مهر اقتصاد': '/bank-logos/Mehr_Eghtesad.svg',
    'مهر ایران': '/bank-logos/Mehr_Iran.svg',
    'ملل': '/bank-logos/Melall.svg',
    'ملت': '/bank-logos/Mellat.svg',
    'ملی': '/bank-logos/Melli.svg',
    'نور': '/bank-logos/Noor.svg',
    'پارسیان': '/bank-logos/Parsian.svg',
    'پاسارگاد': '/bank-logos/Pasargad.svg',
    'پست بانک': '/bank-logos/Postbank.svg',
    'رفاه': '/bank-logos/Refah.svg',
    'رسالت': '/bank-logos/Resalat.svg',
    'صادرات': '/bank-logos/Saderat.svg',
    'سامان': '/bank-logos/Saman.svg',
    'صنعت و معدن': '/bank-logos/Sanat_Madan.svg',
    'سرمایه': '/bank-logos/Sarmayeh.svg',
    'سپه': '/bank-logos/Sepah.svg',
    'شهر': '/bank-logos/Shahr.svg',
    'سینا': '/bank-logos/Sina.svg',
    'استاندارد چارترد': '/bank-logos/Standard_Chartered.svg',
    'تعاون اسلامی': '/bank-logos/Taavon_Eslami.svg',
    'تجارت': '/bank-logos/Tejarat.svg',
    'توسعه': '/bank-logos/Tosee.svg',
    'توسعه صادرات': '/bank-logos/Tosee_Saderat.svg',
    'توسعه تعاون': '/bank-logos/Tosee_Taavon.svg',
    default: '/bank-logos/default.svg',
  };

  // --- توابع کمکی ---
  const persianToEnglishDigits = (str) => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    const englishDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    let result = str || '';
    for (let i = 0; i < persianDigits.length; i++) {
      result = result.replace(new RegExp(persianDigits[i], 'g'), englishDigits[i]);
    }
    return result;
  };

  const formatNumber = (value) => {
    if (!value) return '';
    const rawValue = persianToEnglishDigits(value).replace(/[^\d]/g, '');
    return rawValue ? parseInt(rawValue).toLocaleString('fa-IR') : '';
  };

  const numberToPersianWords = (number) => {
    const units = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه'];
    const tens = ['', 'ده', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود'];
    const hundreds = ['', 'صد', 'دویست', 'سیصد', 'چهارصد', 'پانصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد'];
    const thousands = ['', 'هزار', 'میلیون', 'میلیارد'];

    if (!number || isNaN(number) || number < 0) return 'نامعتبر';
    number = Math.floor(Number(number));
    if (number === 0) return 'صفر';

    const toPersian = (num) => {
      if (num < 10) return units[num];
      if (num < 100) {
        if (num % 10 === 0) return tens[Math.floor(num / 10)];
        return `${tens[Math.floor(num / 10)]} و ${units[num % 10]}`;
      }
      if (num < 1000) {
        const hundred = Math.floor(num / 100);
        const rest = num % 100;
        if (rest === 0) return hundreds[hundred];
        return `${hundreds[hundred]} و ${toPersian(rest)}`;
      }
      return '';
    };

    const formatNumber = (num) => {
      if (num === 0) return 'صفر';
      let result = '';
      let thousandIndex = 0;

      while (num > 0) {
        const part = num % 1000;
        if (part > 0) {
          const partText = toPersian(part);
          result = `${partText} ${thousands[thousandIndex]}${result ? ' و ' + result : ''}`;
        }
        num = Math.floor(num / 1000);
        thousandIndex++;
      }
      return result.trim();
    };

    return formatNumber(number);
  };

  // بالای return، قبل از JSX اضافه کن
const CopyButton = ({ text, label, copiedLabel, onSuccess }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    // روش جدید (مدرن)
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        triggerSuccess();
        return;
      } catch (err) {
        // فال‌بک
      }
    }

    // روش قدیمی (فال‌بک برای iOS و مرورگرهای قدیمی)
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        triggerSuccess();
      } else {
        setError('کپی نشد. لطفاً دستی کپی کنید.');
      }
    } catch (err) {
      setError('کپی نشد. لطفاً دستی کپی کنید.');
    } finally {
      document.body.removeChild(textArea);
    }
  };

  const triggerSuccess = () => {
    setCopied(true);
    onSuccess?.();
    setTimeout(() => {
      setCopied(false);
      setSuccess('');
    }, 1500);
  };

  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      animate={copied ? { scale: [1, 1.15, 1] } : {}}
      onClick={copyToClipboard}
      className={`w-full sm:w-auto px-5 py-3 rounded-xl font-bold text-sm md:text-base transition-all flex items-center justify-center gap-2 shadow-md ${
        copied ? 'bg-green-600' : 'bg-indigo-600 hover:bg-indigo-700'
      } text-white`}
    >
      <AnimatePresence mode="wait">
        {copied ? (
          <motion.div
            key="check"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0, rotate: 180 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <Check className="w-4 h-4 md:w-5 md:h-5" />
          </motion.div>
        ) : (
          <motion.div
            key="copy"
            initial={{ scale: 0, rotate: 180 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0, rotate: -180 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <Copy className="w-4 h-4 md:w-5 md:h-5" />
          </motion.div>
        )}
      </AnimatePresence>
      <span>{copied ? copiedLabel : label}</span>
    </motion.button>
  );
};
  const toTomanText = (rial) => {
    if (!rial || isNaN(parseFloat(rial))) return 'نامعتبر';
    const toman = Math.floor(parseFloat(rial) / 10);
    return `${numberToPersianWords(toman)} تومان`;
  };

  const handleAmountChange = (e) => {
    const inputValue = e.target.value;
    const rawValue = persianToEnglishDigits(inputValue).replace(/[^\d]/g, '');
    setAmount(rawValue);
  };

  // --- توابع API ---
  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const res = await api.post('/api/auth/refresh-token', { refreshToken });
      localStorage.setItem('authToken', res.data.accessToken);
      return res.data.accessToken;
    } catch (err) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      navigate('/login_page');
      return null;
    }
  };

  const fetchUserAndBalance = async () => {
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      const res = await api.get('/api/users/me', {
        headers: { Authorization: `Bearer ${newToken}` },
      });
      setUser(res.data.user);
      setBalance(res.data.balance);
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در دریافت اطلاعات کاربر');
      setTimeout(() => setError(''), 3000);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      const res = await api.get('/api/bank-accounts', {
        headers: { Authorization: `Bearer ${newToken}` },
      });
      setBankAccounts(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در دریافت حساب‌های بانکی');
      setTimeout(() => setError(''), 3000);
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      const res = await api.get('/api/wallet/requests/pending', {
        headers: { Authorization: `Bearer ${newToken}` },
      });
      setPendingRequests(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در دریافت درخواست‌های pending');
      setTimeout(() => setError(''), 3000);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login_page');
      return;
    }
    fetchUserAndBalance();
    fetchBankAccounts();
    fetchPendingRequests();
  }, [token, navigate]);

  const handleDeposit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsUploading(true);
    setUploadProgress(0);

    try {
      if (!amount || isNaN(parseFloat(amount))) {
        setError('لطفاً یک مقدار عددی معتبر وارد کنید');
        setTimeout(() => setError(''), 3000);
        setIsUploading(false);
        return;
      }
      if (!receipt) {
        setError('لطفاً فیش واریزی را انتخاب کنید');
        setTimeout(() => setError(''), 3000);
        setIsUploading(false);
        return;
      }

      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      const formData = new FormData();
      formData.append('amount', amount);
      formData.append('receipt', receipt);

      await api.post('/api/wallet/deposit', formData, {
        headers: {
          Authorization: `Bearer ${newToken}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      setSuccess('درخواست شارژ ثبت شد');
      setAmount('');
      setReceipt(null);
      setTimeout(() => setSuccess(''), 3000);
      fetchUserAndBalance();
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در ثبت شارژ');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsUploading(false);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    try {
      if (!amount || isNaN(parseFloat(amount))) {
        setError('لطفاً یک مقدار عددی معتبر وارد کنید');
        setTimeout(() => setError(''), 3000);
        return;
      }
      if (parseFloat(amount) > balance.balance_rial) {
        setError('موجودی کافی نیست');
        setTimeout(() => setError(''), 3000);
        return;
      }

      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      await api.post('/api/wallet/withdraw', { amount, bank_account_id: bankAccountId }, {
        headers: { Authorization: `Bearer ${newToken}` },
      });
      setSuccess('درخواست برداشت ثبت شد');
      setAmount('');
      setBankAccountId('');
      setTimeout(() => setSuccess(''), 3000);
      fetchUserAndBalance();
      fetchPendingRequests();
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در ثبت برداشت');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      await api.post(`/api/wallet/requests/cancel/${requestId}`, {}, {
        headers: { Authorization: `Bearer ${newToken}` },
      });
      setSuccess('درخواست برداشت با موفقیت لغو شد');
      setTimeout(() => setSuccess(''), 3000);
      fetchPendingRequests();
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در لغو درخواست');
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatCardNumber = (num) => num.replace(/(\d{4})(?=\d)/g, '$1 - ');
  const formatIban = (iban) => {
    const cleaned = iban.replace(/\s/g, '');
    return cleaned.replace(/(IR\d{2})(\d{4})(\d{4})(\d{4})(\d{4})(\d{4})(\d{2})/, '$1 - $2 - $3 - $4 - $5 - $6 - $7');
  };

  const getBankLogo = (name) => bankLogos[name?.trim()] || bankLogos.default;

  // --- تابع کپی با انیمیشن ---
  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'card') {
        setCardCopied(true);
        setSuccess('شماره کارت کپی شد!');
      } else if (type === 'sheba') {
        setShebaCopied(true);
        setSuccess('شماره شبا کپی شد!');
      }
      setTimeout(() => {
        setSuccess('');
        if (type === 'card') setCardCopied(false);
        if (type === 'sheba') setShebaCopied(false);
      }, 1500);
    } catch (err) {
      setError('خطا در کپی کردن');
      setTimeout(() => setError(''), 2000);
    }
  };

  // --- JSX ---
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 px-4 py-8 font-vazir">
      <div className="max-w-5xl mx-auto">

        {/* پیام‌ها */}
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4">
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -100, opacity: 0 }}
                transition={{ type: "spring", stiffness: 120 }}
                className="mb-3"
              >
                <div className="bg-red-500/90 backdrop-blur-lg text-white px-6 py-4 rounded-2xl shadow-2xl border border-red-300 flex items-center gap-3">
                  <AlertCircle className="w-6 h-6" />
                  <span className="font-medium">{error}</span>
                </div>
              </motion.div>
            )}
            {success && (
              <motion.div
                initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -100, opacity: 0 }}
                transition={{ type: "spring", stiffness: 120 }}
              >
                <div className="bg-green-500/90 backdrop-blur-lg text-white px-6 py-4 rounded-2xl shadow-2xl border border-green-300 flex items-center gap-3">
                  <CheckCircle className="w-6 h-6" />
                  <span className="font-medium">{success}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* عنوان */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-bold text-center bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-10"
        >
          کیف پول هوشمند
        </motion.h2>

        {/* موجودی */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10"
        >
          <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-6 shadow-xl border border-white/50">
            <p className="text-gray-600 text-sm mb-2">موجودی ریالی</p>
            <p className="text-3xl font-bold text-indigo-700">
              {parseFloat(balance.balance_rial).toLocaleString('fa-IR')} ریال
            </p>
            <p className="text-sm text-indigo-600 mt-1">
              ≈ {Math.floor(balance.balance_rial / 10).toLocaleString('fa-IR')} تومان
            </p>
          </div>
          <div className="bg-gradient-to-br from-amber-100 to-yellow-100 rounded-3xl p-6 shadow-xl border border-amber-200">
            <p className="text-gray-700 text-sm mb-2">موجودی طلایی</p>
            <p className="text-3xl font-bold text-amber-800">
              {parseFloat(balance.balance_gold).toFixed(3)} گرم
            </p>
          </div>
        </motion.div>

        {/* تب‌ها و فرم‌ها */}
        <div className="bg-white/80 backdrop-blur-2xl rounded-3xl shadow-2xl p-6 md:p-10 border border-gray-100">
          {/* تب‌ها */}
          <div className="flex justify-center mb-8">
            <div className="flex bg-gray-100 p-1.5 rounded-2xl shadow-inner overflow-x-auto scrollbar-hide">
              {[
                { id: 'deposit', label: 'شارژ کیف پول', icon: ArrowUpCircle, color: 'from-green-500 to-emerald-600' },
                { id: 'withdraw', label: 'برداشت از کیف پول', icon: ArrowDownCircle, color: 'from-red-500 to-rose-600' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="relative flex-1 px-6 py-4 rounded-xl font-bold text-sm md:text-lg flex items-center gap-2 md:gap-3 justify-center overflow-hidden transition-all duration-300"
                >
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="tab-indicator"
                      className={`absolute inset-0 bg-gradient-to-r ${tab.color} rounded-xl shadow-lg pointer-events-none z-0`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ type: "spring", stiffness: 350, damping: 30 }}
                    />
                  )}
                  <div className="flex items-center gap-2 md:gap-3 z-10 relative">
                    <tab.icon className={`w-5 h-5 md:w-6 md:h-6 ${activeTab === tab.id ? 'text-white' : 'text-gray-600'}`} />
                    <span className={`whitespace-nowrap ${activeTab === tab.id ? 'text-white' : 'text-gray-700'}`}>
                      {tab.label}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* محتوای تب */}
          <AnimatePresence mode="wait">
            {activeTab === 'deposit' && (
              <motion.div
                key="deposit"
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 50, opacity: 0 }}
                className="space-y-6"
              >
                {/* === بخش اطلاعات واریز === */}
                <motion.div
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.1 }}
  className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-4 md:p-6 shadow-md"
>
  <h3 className="text-base md:text-lg font-bold text-indigo-800 mb-3 md:mb-4 flex items-center gap-1.5 md:gap-2">
    <CreditCard className="w-5 h-5 md:w-6 md:h-6" />
    حساب مقصد واریز
  </h3>

  {/* هشدار */}
  <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 md:p-4 mb-4 text-amber-900 text-xs md:text-sm leading-relaxed font-medium text-center">
    **توجه:**
    <br/>
     واریز فقط از حساب **به نام خودتان** پذیرفته می‌شود.  
     <br/>
    در صورت مغایرت، مبلغ پس از **۷۲ ساعت** عودت می‌شود.
  </div>

  {/* شماره کارت */}
  <div className="mb-4">
    <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5">شماره کارت</label>
    <div className="flex flex-col sm:flex-row gap-2">
      <div className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2.5 font-mono text-xs md:text-base tracking-wider text-center sm:text-left break-all" dir="ltr">
        {formatCardNumber('6219861405418871')}
      </div>
      <CopyButton
        text="6219861405418871"
        label="کپی"
        copiedLabel="کپی شد!"
        onSuccess={() => setSuccess('شماره کارت کپی شد!')}
      />
    </div>
    
  </div>

  {/* شماره شبا */}
  <div>
    <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5">شماره شبا</label>
    <div className="flex flex-col sm:flex-row gap-2">
      <div className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2.5 font-mono text-xs md:text-base tracking-wider text-center sm:text-left break-all" dir="ltr">
        {formatIban('IR910560611828006882072801')}
      </div>
      <CopyButton
        text="910560611828006882072801"
        label="کپی"
        copiedLabel="کپی شد!"
        onSuccess={() => setSuccess('شماره شبا کپی شد!')}
      />
    </div>
    
  </div>

  <div className="mt-4 pt-3 border-t border-blue-200">
    <p className="text-xs md:text-sm text-gray-600 text-center sm:text-left">
      <strong>به نام:</strong> مهدی خرمی
    </p>
  </div>
</motion.div>
                {/* === پایان بخش اطلاعات واریز === */}

                {/* فرم شارژ */}
                <form onSubmit={handleDeposit} className="space-y-6">
                  <div>
                    <label className="block text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <CreditCard className="w-6 h-6 text-green-600" />
                      مبلغ شارژ (ریال)
                    </label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={amount ? formatNumber(amount) : ''}
                      onChange={handleAmountChange}
                      className="w-full px-6 py-5 text-xl font-mono text-left border-2 border-gray-300 rounded-2xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all"
                      placeholder="100,000"
                      dir="ltr"
                    />
                    {amount && (
                      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-lg text-green-600 mt-3 font-bold text-left">
                        {toTomanText(amount)}
                      </motion.p>
                    )}
                  </div>

                  <div>
                    <label className="block text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <Upload className="w-6 h-6 text-indigo-600" />
                      آپلود فیش واریزی
                    </label>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={(e) => setReceipt(e.target.files[0])}
                      className="w-full p-6 border-2 border-dashed border-gray-300 rounded-2xl file:mr-4 file:py-4 file:px-8 file:rounded-full file:border-0 file:text-sm file:font-bold file:bg-gradient-to-r file:from-indigo-600 file:to-purple-600 file:text-white hover:file:from-indigo-700 hover:file:to-purple-700 transition-all text-gray-700"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isUploading}
                    className="w-full py-5 rounded-2xl font-bold text-xl text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-xl hover:shadow-2xl transform hover:scale-[1.02] transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                  >
                    <ArrowUpCircle className="w-7 h-7" />
                    {isUploading ? `در حال آپلود... ${uploadProgress}%` : 'ثبت درخواست شارژ'}
                  </button>
                </form>
              </motion.div>
            )}

            {/* تب برداشت */}
            {activeTab === 'withdraw' && (
              <motion.div key="withdraw" initial={{ x: 50, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -50, opacity: 0 }} className="space-y-6">
                {pendingRequests.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 p-5 rounded-2xl">
                    <h4 className="font-bold text-amber-800 mb-3 text-lg">در انتظار تأیید</h4>
                    {pendingRequests.map((req) => (
                      <div key={req.id} className="bg-white p-4 rounded-xl shadow mb-3 flex justify-between items-center">
                        <div>
                          <p className="font-bold text-lg">{parseFloat(req.amount).toLocaleString('fa-IR')} ریال</p>
                          <p className="text-sm text-gray-500">{new Date(req.created_at).toLocaleString('fa-IR')}</p>
                        </div>
                        <button onClick={() => handleCancelRequest(req.id)} className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl text-sm font-bold transition">
                          لغو
                        </button>
                      </div>
                    ))}
                  </motion.div>
                )}

                <form onSubmit={handleWithdraw} className="space-y-6">
                  <div>
                    <label className="block text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <CreditCard className="w-6 h-6 text-red-600" />
                      مبلغ برداشت (ریال)
                    </label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={formatNumber(amount)}
                      onChange={handleAmountChange}
                      className="w-full px-6 py-5 text-xl font-mono text-left border-2 border-gray-300 rounded-2xl focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all"
                      placeholder="50,000"
                      dir="ltr"
                    />
                    {amount && <p className="text-lg text-red-600 mt-3 font-bold text-left">{toTomanText(amount)}</p>}
                  </div>

                  <div>
                    <label className="block text-lg font-bold text-gray-800 mb-4">حساب مقصد</label>
                    {bankAccounts.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-300">
                        <div className="bg-gray-200 border-2 border-dashed rounded-xl w-24 h-24 mx-auto mb-4" />
                        <p className="text-gray-600 font-bold">حسابی ثبت نشده</p>
                      </div>
                    ) : (
                      <div className="grid gap-5 sm:grid-cols-1 md:grid-cols-2">
                        {bankAccounts.map((account) => {
                          const isSelected = bankAccountId === account.id;
                          return (
                            <motion.label
                              key={account.id}
                              whileHover={{ scale: 1.03 }}
                              whileTap={{ scale: 0.98 }}
                              className={`relative block cursor-pointer rounded-3xl p-6 transition-all duration-300 overflow-hidden border-2 backdrop-blur-sm ${
                                isSelected
                                  ? 'border-red-600 bg-gradient-to-br from-red-50 to-rose-50 shadow-2xl'
                                  : 'border-gray-200 bg-white/70 hover:border-red-400 hover:shadow-xl'
                              }`}
                              onClick={() => setBankAccountId(account.id)}
                            >
                              <input type="radio" name="bankAccount" className="sr-only" />
                              {isSelected && (
                                <motion.div layoutId="selected-check" className="absolute top-4 left-4 w-12 h-12 bg-gradient-to-r from-red-600 to-rose-600 rounded-full flex items-center justify-center shadow-xl">
                                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                </motion.div>
                              )}
                              <div className="flex items-center gap-5">
                                <img
                                  src={getBankLogo(account.bank_name)}
                                  alt={account.bank_name}
                                  className="w-20 h-20 object-contain rounded-2xl bg-white p-3 shadow-lg border"
                                  onError={(e) => (e.target.src = bankLogos.default)}
                                />
                                <div className="flex-1">
                                  <p className={`font-bold text-lg ${isSelected ? 'text-red-700' : 'text-gray-800'}`}>
                                    {account.bank_name}
                                  </p>
                                  <p className="text-sm text-gray-600">{account.account_holder_name}</p>
                                  <p className="font-mono text-sm text-gray-700 mt-2" dir="ltr">
                                    {formatCardNumber(account.card_number)}
                                  </p>
                                  <p className="font-mono text-xs text-gray-500" dir="ltr">
                                    {formatIban(account.iban)}
                                  </p>
                                </div>
                              </div>
                            </motion.label>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={!bankAccountId}
                    className="w-full py-5 rounded-2xl font-bold text-xl text-white bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 shadow-xl hover:shadow-2xl transform hover:scale-[1.02] transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                  >
                    <ArrowDownCircle className="w-7 h-7" />
                    ثبت درخواست برداشت
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* اسپینر آپلود */}
        <AnimatePresence>
          {isUploading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-3xl p-10 shadow-2xl text-center"
              >
                <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-green-600 mx-auto mb-6"></div>
                <p className="text-2xl font-bold text-gray-800">{uploadProgress}%</p>
                <p className="text-gray-600 mt-2">در حال آپلود فیش واریزی...</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}